import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { theme } from '@garnish/constants';
import {
  Carousel,
  LoadingPlaceholder,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { useTelemetry } from '@order/Telemetry';

import { NotFoundView } from '../../../../components';
import {
  ScanInStoreCta,
  ScanInStoreTabContentContainer,
} from '../../components/';
import { useScanInStoreGiftCards } from '../../hooks';
import { ScanInStorePayEmptyState, ScanInStorePayGiftCard } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const ScanInStorePay = () => {
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  const {
    isLoadingGiftCards,
    hasGiftCardsError,
    giftCards,
    hasGiftCardBalance,
  } = useScanInStoreGiftCards();

  // ─── Dynamic Styles ──────────────────────────────────────────────────

  const isSingleGiftCard = giftCards.length === 1;
  const ctaGap = isSingleGiftCard ? styles.singleGiftCardGap : undefined;

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToAddGiftCard = useCallback(() => {
    track('scan_addgiftcard_cta.press');

    if (isExpoRouterEnabled) {
      router?.navigate('/add-gift-card');
      return;
    }

    navigation?.navigate('Modal', { screen: 'AddGiftCard' });
  }, [isExpoRouterEnabled, router, navigation, track]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ScanInStoreTabContentContainer>
      <Carousel
        navControlAccessibilityLabel={formatMessage(messages.a11yLabel)}
      >
        {hasGiftCardBalance ? (
          giftCards.map((giftCard) => (
            <ScanInStorePayGiftCard
              key={giftCard.id}
              qrCodeValue={giftCard.id}
              name={giftCard.title}
              balance={giftCard.remainingAmount}
            />
          ))
        ) : (
          <ScanInStorePayEmptyContent
            isLoadingGiftCards={isLoadingGiftCards}
            hasGiftCardsError={hasGiftCardsError}
            hasGiftCardBalance={hasGiftCardBalance}
            onPressCta={navigateToAddGiftCard}
          />
        )}
      </Carousel>

      {hasGiftCardBalance ? (
        <ScanInStoreCta containerStyle={ctaGap} onPress={navigateToAddGiftCard}>
          <FormattedMessage {...messages.title} />
        </ScanInStoreCta>
      ) : null}
    </ScanInStoreTabContentContainer>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ScanInStorePayEmptyContent = (props: ScanInStorePayEmptyContentProps) => {
  const {
    isLoadingGiftCards,
    hasGiftCardsError,
    hasGiftCardBalance,
    onPressCta,
  } = props;

  // ─── Loading State ───────────────────────────────────────────────────

  if (isLoadingGiftCards) {
    return <LoadingPlaceholder rowHeight={CAROUSEL_HEIGHT} palette="cream" />;
  }

  // ─── Error State ─────────────────────────────────────────────────────

  if (hasGiftCardsError) {
    return <NotFoundView withoutFooter />;
  }

  // ─── Empty State ─────────────────────────────────────────────────────

  if (!hasGiftCardBalance) {
    return <ScanInStorePayEmptyState onPressCta={onPressCta} />;
  }
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Add gift card',
    description: 'Scan In Store Pay | Title',
  },
  a11yLabel: {
    defaultMessage: 'Gift card',
    description: 'Scan In Store Pay | a11y Label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CAROUSEL_HEIGHT = 332;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  singleGiftCardGap: {
    marginTop: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ScanInStorePayEmptyContentProps = Readonly<{
  isLoadingGiftCards: boolean;
  hasGiftCardsError: boolean;
  hasGiftCardBalance: boolean;
  onPressCta: () => void;
}>;
